import { games } from './games'

const HOME_PATH = '/'
const SETS_INDEX_PATH = '/sets'
const SITE_ORIGIN = 'https://holic.net'

type LinkRef = Readonly<{
  href: string
  label: string
}>

type PageLink = LinkRef & Readonly<{
  current: boolean
  page: number
  rel?: string
}>

type Crumb = Readonly<{
  name: string
  path: string
}>

type NamedTag = Readonly<{
  id: number
  parentId?: number
  depth: number
  type?: string
  nameEn?: string
  nameJa?: string
  nameEnShort?: string
  nameJaShort?: string
  parent?: NamedTag
  parents?: readonly NamedTag[]
}>

type NamedItem = Readonly<{
  id: number
  nameEn?: string
  nameJa?: string
  nameEnShort?: string
  nameJaShort?: string
  tags: readonly NamedTag[]
}>

type UnitLinkSource = Readonly<{
  id?: number
  userId?: number
  listings?: readonly Readonly<{ priceJpy: number, soldAt?: string }>[]
}>

type CatalogGroup = Readonly<{
  era: NamedTag
  sets: readonly NamedTag[]
}>

type NamedNode = Readonly<{
  nameEn?: string
  nameJa?: string
  nameEnShort?: string
  nameJaShort?: string
}>

const categoryPath = (id: number) => `/categories/${id}`

const cardPath = (categoryId: number, itemId: number) => `/categories/${categoryId}/items/${itemId}`

const unitPath = (userId: number, unitId: number) => `/u/${userId}/units/${unitId}`

const localizedName = (node: NamedNode, language: string): string => {
  if (language.startsWith('ja')) {
    return node.nameJa || node.nameJaShort || node.nameEn || node.nameEnShort || ''
  }

  return node.nameEn || node.nameEnShort || node.nameJa || node.nameJaShort || ''
}

const orderedAncestors = (tag: NamedTag): NamedTag[] => {
  if (tag.parents && tag.parents.length > 0) {
    const byId = new Map(tag.parents.map(parent => [parent.id, parent]))
    const chain: NamedTag[] = []
    const seen = new Set<number>()
    let parentId = tag.parentId

    while (parentId && !seen.has(parentId)) {
      const parent = byId.get(parentId)
      if (!parent) break
      seen.add(parentId)
      chain.unshift(parent)
      parentId = parent.parentId
    }

    if (chain.length > 0) return chain

    return [...tag.parents].sort((left, right) => left.depth - right.depth)
  }

  const chain: NamedTag[] = []
  const seen = new Set<number>()
  let current = tag.parent

  while (current && !seen.has(current.id)) {
    seen.add(current.id)
    chain.unshift(current)
    current = current.parent
  }

  return chain
}

const indexTags = (tags: readonly NamedTag[]): Map<number, NamedTag> => {
  const byId = new Map<number, NamedTag>()

  const visit = (tag?: NamedTag, stack: Set<number> = new Set()) => {
    if (!tag || stack.has(tag.id)) return
    stack.add(tag.id)

    const existing = byId.get(tag.id)
    if (!existing || (tag.parents?.length ?? 0) > (existing.parents?.length ?? 0)) byId.set(tag.id, tag)

    tag.parents?.forEach(parent => visit(parent, stack))
    visit(tag.parent, stack)
    stack.delete(tag.id)
  }

  tags.forEach(tag => visit(tag))
  return byId
}

const parentChain = (tag: NamedTag, byId: ReadonlyMap<number, NamedTag>): NamedTag[] => {
  const parents: NamedTag[] = []
  const seen = new Set<number>([tag.id])
  let parentId = tag.parentId

  while (parentId && !seen.has(parentId)) {
    const parent = byId.get(parentId)
    if (!parent) break
    seen.add(parent.id)
    parents.push(parent)
    parentId = parent.parentId
  }

  return parents
}

const categoryWithAncestry = (tags: readonly NamedTag[], fetched?: NamedTag): NamedTag | undefined => {
  const category = deepestCategory(tags)
  if (!category) return undefined

  const sources = fetched && fetched.id === category.id ? [...tags, fetched] : tags
  const parents = parentChain(category, indexTags(sources))

  return parents.length > 0 ? { ...category, parents } : category
}

const categoryAncestryComplete = (category: NamedTag): boolean => {
  const parents = category.parents ?? []
  if (parents.length === 0) return category.parentId === undefined
  return parents[parents.length - 1].parentId === undefined
}

const deepestCategory = (tags: readonly NamedTag[], preferredId?: number): NamedTag | undefined => {
  const categories = tags.filter(tag => (tag.type ?? 'Category') === 'Category')
  if (categories.length === 0) return undefined

  const maxDepth = categories.reduce((max, tag) => tag.depth > max ? tag.depth : max, 0)
  const deepest = categories.filter(tag => tag.depth === maxDepth)

  if (preferredId !== undefined) {
    const preferred = deepest.find(tag => tag.id === preferredId)
    if (preferred) return preferred
  }

  return deepest[0]
}

const categoryCrumbs = (category: NamedTag, language: string): Crumb[] => {
  const crumbs: Crumb[] = []

  for (const ancestor of orderedAncestors(category)) {
    const name = localizedName(ancestor, language)
    if (!name) continue
    crumbs.push({ name, path: categoryPath(ancestor.id) })
  }

  const name = localizedName(category, language)
  if (name) crumbs.push({ name, path: categoryPath(category.id) })

  return crumbs
}

const setBreadcrumbs = (tag: NamedTag, language: string, homeName: string): Crumb[] => {
  return [
    { name: homeName, path: HOME_PATH },
    ...categoryCrumbs(tag, language),
  ]
}

const categoryPageCrumbs = (
  routeId: string | undefined,
  tag: NamedTag | undefined,
  language: string,
  homeName: string,
): Crumb[] => {
  if (!routeId || !tag) return []
  return setBreadcrumbs(tag, language, homeName)
}

const cardBreadcrumbs = (
  item: NamedItem,
  language: string,
  homeName: string,
  preferredCategoryId?: number,
): Crumb[] => {
  const crumbs: Crumb[] = [{ name: homeName, path: HOME_PATH }]
  const category = deepestCategory(item.tags, preferredCategoryId)

  if (category) crumbs.push(...categoryCrumbs(category, language))

  const name = localizedName(item, language)
  if (name && category) crumbs.push({ name, path: cardPath(category.id, item.id) })

  return crumbs
}

const listingBreadcrumbs = (args: Readonly<{
  item: NamedItem
  language: string
  homeName: string
  listingName: string
  userId?: number
  unitId?: number
  preferredCategoryId?: number
}>): Crumb[] => {
  const crumbs = cardBreadcrumbs(args.item, args.language, args.homeName, args.preferredCategoryId)

  if (args.userId && args.unitId) {
    crumbs.push({ name: args.listingName, path: unitPath(args.userId, args.unitId) })
  }

  return crumbs
}

const breadcrumbListJsonLd = (crumbs: readonly Crumb[], origin: string = SITE_ORIGIN) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: `${origin}${crumb.path}`,
    })),
  }
}

const breadcrumbListJsonLdText = (crumbs: readonly Crumb[], origin: string = SITE_ORIGIN) => {
  return JSON.stringify(breadcrumbListJsonLd(crumbs, origin)).replace(/</g, '\\u003c')
}

const pokemonHubRootIds = (gameList: readonly Readonly<{ id: number, name: string }>[] = games): number[] => {
  return gameList.filter(game => game.name.startsWith('pokemontcg')).map(game => game.id)
}

const catalogGroups = (
  eras: readonly NamedTag[],
  sets: readonly NamedTag[],
  rootIds: readonly number[],
): CatalogGroup[] => {
  const roots = new Set(rootIds)
  const pokemonEras = eras.filter(era => era.parentId !== undefined && roots.has(era.parentId))

  return pokemonEras.map(era => ({
    era,
    sets: sets.filter(set => set.parentId === era.id),
  }))
}

const categoryLinks = (tags: readonly NamedTag[], language: string): LinkRef[] => {
  return tags.flatMap(tag => {
    const label = localizedName(tag, language)
    if (!label) return []
    return [{ href: categoryPath(tag.id), label }]
  })
}

const cardLinksFromItems = (
  items: readonly NamedItem[],
  language: string,
  fallbackCategoryId?: number,
): LinkRef[] => {
  return items.flatMap(item => {
    const category = deepestCategory(item.tags, fallbackCategoryId)
    const categoryId = category?.id ?? fallbackCategoryId
    const label = localizedName(item, language)
    if (!categoryId || !label) return []
    return [{ href: cardPath(categoryId, item.id), label }]
  })
}

const unitLinks = (units: readonly UnitLinkSource[], itemName: string): LinkRef[] => {
  if (!itemName) return []

  return units.flatMap(unit => {
    if (!unit.id || !unit.userId) return []
    const active = unit.listings?.find(listing => !listing.soldAt)
    if (!active) return []
    return [{ href: unitPath(unit.userId, unit.id), label: `${itemName} ¥${Math.round(active.priceJpy)}` }]
  })
}

const contextLinkGroups = (args: Readonly<{
  item: NamedItem
  language: string
  related: readonly NamedItem[]
  illustratorCards: readonly NamedItem[]
}>): Readonly<{
  sameSet: LinkRef[]
  illustrators: LinkRef[]
  illustratorCards: LinkRef[]
  characters: LinkRef[]
}> => {
  const category = deepestCategory(args.item.tags)
  const others = (items: readonly NamedItem[]) => items.filter(item => item.id !== args.item.id)

  return {
    sameSet: cardLinksFromItems(others(args.related), args.language, category?.id),
    illustrators: categoryLinks(args.item.tags.filter(tag => tag.type === 'Illustrator'), args.language),
    illustratorCards: cardLinksFromItems(others(args.illustratorCards), args.language, category?.id),
    characters: categoryLinks(args.item.tags.filter(tag => tag.type === 'Character'), args.language),
  }
}

const homeCatalogLinks = (
  eras: readonly NamedTag[],
  sampleSets: readonly NamedTag[],
  sampleCards: readonly NamedItem[],
  language: string,
  rootIds: readonly number[],
): Readonly<{ eras: LinkRef[], sets: LinkRef[], cards: LinkRef[] }> => {
  const pokemonEras = catalogGroups(eras, [], rootIds).map(group => group.era)
  const firstSet = sampleSets[0]

  return {
    eras: categoryLinks(pokemonEras, language),
    sets: categoryLinks(sampleSets, language),
    cards: cardLinksFromItems(sampleCards, language, firstSet?.id),
  }
}

const readPage = (search: string, param = 'page'): number => {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
  const page = Number(params.get(param))
  if (!Number.isInteger(page) || page < 1) return 1
  return page
}

const paginationWindow = (current: number, total: number): number[] => {
  if (total <= 1) return []

  const pages = new Set<number>([1, total, current - 1, current, current + 1])
  return Array.from(pages).filter(page => page >= 1 && page <= total).sort((left, right) => left - right)
}

const paginationLinks = (pathname: string, search: string, current: number, total: number, param = 'page'): PageLink[] => {
  const base = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)

  return paginationWindow(current, total).map(page => {
    const params = new URLSearchParams(base)
    if (page <= 1) params.delete(param)
    else params.set(param, String(page))

    let rel: string | undefined
    if (page === current + 1) rel = 'next'
    if (page === current - 1) rel = 'prev'

    const query = params.toString()

    return {
      page,
      label: String(page),
      current: page === current,
      rel,
      href: query ? `${pathname}?${query}` : pathname,
    }
  })
}

const collectPages = async <T,>(
  fetchPage: (page: number) => Promise<Readonly<{ tags: readonly T[], totalPages: number }>>,
  maxPages = 20,
): Promise<T[]> => {
  const collected: T[] = []
  let page = 1
  let totalPages = 1

  while (page <= totalPages && page <= maxPages) {
    const result = await fetchPage(page)
    collected.push(...result.tags)
    totalPages = Number.isFinite(result.totalPages) && result.totalPages > 0 ? result.totalPages : page
    page += 1
  }

  return collected
}

const hopsTo = (
  graph: Readonly<Record<string, readonly string[]>>,
  start: string,
  predicate: (path: string) => boolean,
): number | undefined => {
  const queue: Array<{ path: string, hops: number }> = [{ path: start, hops: 0 }]
  const seen = new Set<string>([start])

  while (queue.length > 0) {
    const current = queue.shift()
    if (!current) break
    if (predicate(current.path)) return current.hops

    for (const next of graph[current.path] ?? []) {
      if (seen.has(next)) continue
      seen.add(next)
      queue.push({ path: next, hops: current.hops + 1 })
    }
  }

  return undefined
}

const orphanedPaths = (
  graph: Readonly<Record<string, readonly string[]>>,
  required: readonly string[],
): string[] => {
  const linked = new Set<string>()

  for (const targets of Object.values(graph)) {
    for (const target of targets) linked.add(target)
  }

  return required.filter(path => path !== HOME_PATH && !linked.has(path))
}

export {
  HOME_PATH,
  SETS_INDEX_PATH,
  SITE_ORIGIN,
  breadcrumbListJsonLd,
  breadcrumbListJsonLdText,
  cardBreadcrumbs,
  cardLinksFromItems,
  cardPath,
  catalogGroups,
  categoryAncestryComplete,
  categoryLinks,
  categoryPageCrumbs,
  categoryPath,
  categoryWithAncestry,
  collectPages,
  contextLinkGroups,
  deepestCategory,
  homeCatalogLinks,
  hopsTo,
  listingBreadcrumbs,
  localizedName,
  orderedAncestors,
  orphanedPaths,
  paginationLinks,
  pokemonHubRootIds,
  readPage,
  setBreadcrumbs,
  unitLinks,
  unitPath,
}
export type {
  CatalogGroup,
  Crumb,
  LinkRef,
  NamedItem,
  NamedTag,
  PageLink,
  UnitLinkSource,
}
